/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR320: S1.2 Diagnostics Dashboard v1
 * 
 * Main panel for S1.2 diagnostics details.
 * Tucked under the S1.2 OCR step card and rendered inline (no nested accordion).
 * Self-sufficient: resolves flyerScanRunId from the S1_OCR step attempt diagnostics.
 */

import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useS12DiagnosticsData } from "@/hooks/admin/useS12DiagnosticsData";
import { S12OverviewSection } from "./S12OverviewSection";
import { S12CurrentOpSection } from "./S12CurrentOpSection";
import { OcrRunControlButtons } from "../OcrRunControlButtons";
import { getOcrEngineFromConfig, getOcrEngineLabel } from "@/lib/batch-runs/ocrEngine";

// CR458: Truth-sourced counters
export interface TruthCounts {
  renderReady: number;
  ocrComplete: number; // terminal OCR states (SUCCEEDED + FAILED)
  ocrSucceeded: number;
  ocrFailed: number;
  ocrRunning: number;
  ocrQueued: number;
}

export interface OcrFailedPageRow {
  page_number: number;
  ocr_error_code: string | null;
  primary_ocr_error: string | null;
  ocr_finished_at: string | null;
}

interface S12DiagnosticsPanelProps {
  batchRunId: string;
  isRunning: boolean;
}

export function S12DiagnosticsPanel({
  batchRunId,
  isRunning,
}: S12DiagnosticsPanelProps) {
  const [flyerScanRunId, setFlyerScanRunId] = useState<string | null>(null);
  const [s1OcrAttemptId, setS1OcrAttemptId] = useState<string | null>(null);
  // CR467: Proof badge — last attempt started_at
  const [lastAttemptStarted, setLastAttemptStarted] = useState<string | null>(null);
  const [lastAttemptNo, setLastAttemptNo] = useState<number | null>(null);
  const [attemptEngine, setAttemptEngine] = useState<string | null>(null);
  const [attemptDiscovery, setAttemptDiscovery] = useState<{
    pagesDiscoveredTotal: number | null;
    pagesSeeded: number | null;
    truthPagesFound: number | null;
    captureExpectedTotal: number | null;
    registryPages: number | null;
  }>({
    pagesDiscoveredTotal: null,
    pagesSeeded: null,
    truthPagesFound: null,
    captureExpectedTotal: null,
    registryPages: null,
  });
  // CR458: Truth-sourced counters
  const [truthCounts, setTruthCounts] = useState<TruthCounts>({
    renderReady: 0,
    ocrComplete: 0,
    ocrSucceeded: 0,
    ocrFailed: 0,
    ocrRunning: 0,
    ocrQueued: 0,
  });
  const [failedPages, setFailedPages] = useState<OcrFailedPageRow[]>([]);
  const [postSeedSeenOnce, setPostSeedSeenOnce] = useState(false);

  // Resolve flyerScanRunId from the S1_OCR step attempt diagnostics
  useEffect(() => {
    const resolve = async () => {
      // CR643: Resolve latest attempt first; only use its run id when present.
      const { data } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, diagnostics_json, config_json, started_at, attempt_no, status")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .order("created_at", { ascending: false })
        .order("attempt_no", { ascending: false })
        .limit(20);

      const attempts = (data ?? []) as Array<{
        id?: string | null;
        diagnostics_json?: Record<string, unknown> | null;
        config_json?: Record<string, unknown> | null;
        started_at?: string | null;
        attempt_no?: number | null;
      }>;
      const latest = attempts[0];
      const best = latest;
      setS1OcrAttemptId(typeof best?.id === "string" && best.id.length > 0 ? best.id : null);

      const diag = (best?.diagnostics_json ?? {}) as Record<string, unknown>;
      const runId = diag.flyer_scan_run_id;
      if (typeof runId === "string" && runId.length > 0) {
        setFlyerScanRunId(runId);
      } else {
        setFlyerScanRunId(null);
      }
      // CR467: Proof badge
      if (best?.started_at) setLastAttemptStarted(best.started_at);
      if (best?.attempt_no) setLastAttemptNo(best.attempt_no);

      const discovery = (diag.discovery ?? {}) as Record<string, unknown>;
      setAttemptDiscovery({
        pagesDiscoveredTotal:
          typeof discovery.pages_discovered_total === "number" ? discovery.pages_discovered_total : null,
        pagesSeeded: typeof discovery.pages_seeded === "number" ? discovery.pages_seeded : null,
        truthPagesFound: typeof discovery.truth_pages_found === "number" ? discovery.truth_pages_found : null,
        captureExpectedTotal:
          typeof discovery.capture_expected_total === "number" ? discovery.capture_expected_total : null,
        registryPages:
          typeof discovery.registry_pages === "number" ? discovery.registry_pages : null,
      });

      const cfg = (best?.config_json ?? {}) as Record<string, unknown>;
      const diagCfg = ((best?.diagnostics_json ?? {}).config_snapshot ?? {}) as Record<string, unknown>;
      const flyerExtractionCfg = (cfg.flyerExtraction as Record<string, unknown> | undefined) ?? null;
      let batchFlyerExtractionCfg: Record<string, unknown> | null = null;
      if (!getOcrEngineFromConfig(diagCfg) && !getOcrEngineFromConfig(cfg) && !getOcrEngineFromConfig(flyerExtractionCfg)) {
        const { data: runCfg } = await supabase
          .from("cr075_batch_runs")
          .select("config_json")
          .eq("id", batchRunId)
          .single();
        batchFlyerExtractionCfg = ((runCfg?.config_json as Record<string, unknown> | undefined)?.flyerExtraction as Record<string, unknown> | undefined) ?? null;
      }
      const resolvedEngine =
        getOcrEngineFromConfig(diagCfg) ??
        getOcrEngineFromConfig(cfg) ??
        getOcrEngineFromConfig(flyerExtractionCfg) ??
        getOcrEngineFromConfig(batchFlyerExtractionCfg) ??
        null;
      setAttemptEngine(resolvedEngine);
    };
    resolve();
    if (isRunning) {
      const interval = setInterval(resolve, 5000);
      return () => clearInterval(interval);
    }
  }, [batchRunId, isRunning]);

  // CR461: Lightweight count-based truth polling (no full row fetch)
  useEffect(() => {
    const fetchTruthCounts = async () => {
      const truthBase = supabase.from("cr075_flyer_page_truth" as any);
      const renderRes = await truthBase
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .eq("rendered", true)
        .not("render_storage_path", "is", null);

      // CR660: S1.2 now writes OCR terminal state to flyer_scan_pages_v2 (run_id = S1_OCR attempt id),
      // not to cr075_flyer_page_truth. Count OCR terminal completions from the canonical queue surface.
      let ocrComplete = 0;
      let ocrSucceeded = 0;
      let ocrFailed = 0;
      let ocrRunning = 0;
      let ocrQueued = 0;
      if (s1OcrAttemptId) {
        const pagesBase = supabase.from("flyer_scan_pages_v2");
        const [terminalRes, succeededRes, failedRes, runningRes, queuedRes] = await Promise.all([
          pagesBase
            .select("id", { count: "exact", head: true })
            .eq("run_id", s1OcrAttemptId)
            .in("ocr_status", ["SUCCEEDED", "FAILED"]),
          pagesBase
            .select("id", { count: "exact", head: true })
            .eq("run_id", s1OcrAttemptId)
            .eq("ocr_status", "SUCCEEDED"),
          pagesBase
            .select("id", { count: "exact", head: true })
            .eq("run_id", s1OcrAttemptId)
            .eq("ocr_status", "FAILED"),
          pagesBase
            .select("id", { count: "exact", head: true })
            .eq("run_id", s1OcrAttemptId)
            .eq("ocr_status", "RUNNING"),
          pagesBase
            .select("id", { count: "exact", head: true })
            .eq("run_id", s1OcrAttemptId)
            .eq("ocr_status", "QUEUED"),
        ]);
        ocrComplete = terminalRes.count ?? 0;
        ocrSucceeded = succeededRes.count ?? 0;
        ocrFailed = failedRes.count ?? 0;
        ocrRunning = runningRes.count ?? 0;
        ocrQueued = queuedRes.count ?? 0;
      } else {
        const [legacyOcrRes, legacyFailedRes] = await Promise.all([
          truthBase
            .select("id", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId)
            .eq("ocr_done", true),
          truthBase
            .select("id", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId)
            .eq("outcome", "OCR_FAILED"),
        ]);
        ocrComplete = legacyOcrRes.count ?? 0;
        ocrSucceeded = Math.max(0, ocrComplete - (legacyFailedRes.count ?? 0));
        ocrFailed = legacyFailedRes.count ?? 0;
      }

      setTruthCounts({
        renderReady: renderRes.count ?? 0,
        ocrComplete,
        ocrSucceeded,
        ocrFailed,
        ocrRunning,
        ocrQueued,
      });
    };
    fetchTruthCounts();
    if (isRunning) {
      const interval = setInterval(fetchTruthCounts, 5000);
      return () => clearInterval(interval);
    }
  }, [batchRunId, isRunning, s1OcrAttemptId]);

  useEffect(() => {
    const fetchFailedPages = async () => {
      if (!s1OcrAttemptId) {
        setFailedPages([]);
        return;
      }
      const { data } = await supabase
        .from("flyer_scan_pages_v2")
        .select("page_number, ocr_error_code, primary_ocr_error, ocr_finished_at")
        .eq("run_id", s1OcrAttemptId)
        .eq("ocr_status", "FAILED")
        .order("page_number", { ascending: true })
        .limit(500);
      setFailedPages((data ?? []) as OcrFailedPageRow[]);
    };
    fetchFailedPages();
    if (isRunning) {
      const interval = setInterval(fetchFailedPages, 5000);
      return () => clearInterval(interval);
    }
  }, [isRunning, s1OcrAttemptId]);

  // CR646: pass attempt start time so diagnostics hook can resolve active run
  // even when flyer_scan_run_id is absent/mis-keyed in step diagnostics.
  const diagnostics = useS12DiagnosticsData(
    flyerScanRunId,
    isRunning,
    batchRunId,
    s1OcrAttemptId,
    lastAttemptStarted
  );
  const {
    runData,
    events,
    resolvedRunId,
    noProgressSeconds,
    isLoading,
  } = diagnostics;

  const discoveredPages = attemptDiscovery.pagesDiscoveredTotal;
  const seededPages = attemptDiscovery.pagesSeeded;
  const truthPagesFound = attemptDiscovery.truthPagesFound;
  // CR: diagnostics_json.discovery.truth_pages_found can be stale/zero while
  // truth counters already know render-ready totals. Prefer truth counter when available.
  const resolvedTruthPages =
    (typeof truthPagesFound === "number" && truthPagesFound > 0)
      ? truthPagesFound
      : (truthCounts.renderReady > 0 ? truthCounts.renderReady : truthPagesFound);
  const expectedPages = attemptDiscovery.captureExpectedTotal;
  const expectedPagesMeaningful = expectedPages != null && expectedPages > 0 ? expectedPages : null;
  const hasOcrQueueEvidence =
    truthCounts.ocrComplete > 0 ||
    truthCounts.ocrRunning > 0 ||
    truthCounts.ocrQueued > 0;
  const queueActiveOverride = truthCounts.ocrRunning > 0 || truthCounts.ocrQueued > 0;
  const isPostSeed = (seededPages ?? 0) > 0 || hasOcrQueueEvidence;

  useEffect(() => {
    // Reset one-poll latch per OCR attempt so each run starts in pre-seed state.
    setPostSeedSeenOnce(false);
  }, [s1OcrAttemptId]);

  useEffect(() => {
    // Require one confirmed post-seed poll before warning eligibility.
    if (isPostSeed) setPostSeedSeenOnce(true);
  }, [isPostSeed]);

  const isLogicallyComplete =
    truthCounts.renderReady > 0 &&
    truthCounts.ocrComplete >= truthCounts.renderReady &&
    truthCounts.ocrRunning === 0 &&
    truthCounts.ocrQueued === 0;
  const hasRealPageMismatch =
    postSeedSeenOnce &&
    !isLogicallyComplete && (
      (discoveredPages != null && resolvedTruthPages != null && discoveredPages !== resolvedTruthPages) ||
      (expectedPagesMeaningful != null && resolvedTruthPages != null && expectedPagesMeaningful !== resolvedTruthPages) ||
      (expectedPagesMeaningful != null && discoveredPages != null && expectedPagesMeaningful !== discoveredPages) ||
      (
        discoveredPages != null &&
        seededPages != null &&
        discoveredPages !== seededPages &&
        truthCounts.ocrComplete === 0 &&
        truthCounts.ocrRunning === 0 &&
        truthCounts.ocrQueued === 0
      )
    );

  const formatEngine = (engine?: string | null): string | null => {
    if (!engine) return null;
    return getOcrEngineLabel(engine, engine);
  };
  const snapshotEngine = formatEngine(attemptEngine);
  const runEngine = formatEngine((runData?.primary_ocr_engine as string | null) ?? null);
  const engineLabel = snapshotEngine || runEngine;
  const effectiveRunId = flyerScanRunId || resolvedRunId || undefined;
  const runStatus = String(runData?.status ?? "").toUpperCase();
  const runIsActive = !isLogicallyComplete && (runStatus === "RUNNING" || runStatus === "IN_PROGRESS");
  const canShowForceStop = !isLogicallyComplete && (isRunning || runIsActive);

  // Don't show anything if no run data AND no attempt id AND not running
  if (!flyerScanRunId && !s1OcrAttemptId && !isRunning) return null;
  if (isLoading && !runData && !s1OcrAttemptId) return null;

  return (
    <div className="space-y-1.5">
      {hasRealPageMismatch && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
          Soft warning: S1.0C/S1.2 page mismatch detected.
          {" "}
          discovered={attemptDiscovery.pagesDiscoveredTotal ?? "—"},
          {" "}
          seeded_for_ocr={attemptDiscovery.pagesSeeded ?? "—"},
          {" "}
          registry={attemptDiscovery.registryPages ?? "—"},
          {" "}
          truth_found={resolvedTruthPages ?? "—"},
          {" "}
          expected={expectedPagesMeaningful ?? "—"}.
        </div>
      )}
      {runData ? (
        <div className="p-1.5 space-y-1.5">
          {/* Minimal diagnostics: Overview + Current Operation */}
          <div className="space-y-1.5">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Overview</div>
              <S12OverviewSection
                runData={runData}
                noProgressSeconds={noProgressSeconds}
                truthCounts={truthCounts}
                isLogicallyComplete={isLogicallyComplete}
                queueActiveOverride={queueActiveOverride}
                engineLabel={engineLabel}
                failedPages={failedPages}
                lastAttemptNo={lastAttemptNo}
                lastAttemptStarted={lastAttemptStarted}
                s1OcrAttemptId={s1OcrAttemptId}
                resolvedRunId={resolvedRunId}
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Current Operation</div>
              <S12CurrentOpSection runData={runData} events={events} />
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap pt-2 border-t">
              {canShowForceStop && (
                <OcrRunControlButtons
                  batchRunId={batchRunId}
                  flyerScanRunId={effectiveRunId}
                  isRunning
                />
              )}
            </div>
          </div>
        </div>
      ) : s1OcrAttemptId ? (
        <div className="p-2 space-y-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Attempt Details</div>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant="outline" className="text-xs">Step Attempt {lastAttemptNo || ""}</Badge>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Engine:</span>
                <Badge variant="outline" className="text-xs font-mono">{engineLabel || "—"}</Badge>
              </div>
            </div>
            {/* Show error from step attempt diagnostics if present */}
            {(() => {
              const diag = (diagnostics.runData?.discovery_debug as any) || {};
              const errors = diag.errors || [];
              if (errors.length > 0) {
                return (
                  <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md text-xs">
                    <div className="flex items-center gap-1.5 text-destructive font-medium">
                      <span>Attempt Error:</span>
                    </div>
                    <ul className="list-disc list-inside text-muted-foreground mt-1">
                      {errors.map((err: any, idx: number) => (
                        <li key={idx} className="line-clamp-2">{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                      ))}
                    </ul>
                  </div>
                );
              }
              return (
                <div className="border rounded-lg p-4 text-xs text-muted-foreground text-center">
                  {isRunning
                    ? "Attempt is initializing. Waiting for telemetry run linkage..."
                    : "Scan run failed to start. Check attempt IDs or engine connectivity."}
                </div>
              );
            })()}
            <div className="flex items-center justify-end gap-2 flex-wrap pt-2 border-t">
              {canShowForceStop && (
                <OcrRunControlButtons
                  batchRunId={batchRunId}
                  flyerScanRunId={effectiveRunId}
                  isRunning
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-4 text-xs text-muted-foreground text-center">
          No run data available. Start an OCR scan to see diagnostics.
        </div>
      )}
    </div>
  );
}
